import moment from 'moment'
import { cast } from './cast'
import { Data, Prop, Type, Operator } from './types'

export const evaluate = (
  data: Data,
  formula: boolean | number | string | any[] | Prop,
  outputType: Type,
) => {
  if (
    typeof formula === 'boolean' ||
    typeof formula === 'number' ||
    typeof formula === 'string' ||
    'type' in formula
  ) {
    return cast(formula, outputType)
  }

  if (!formula?.length) {
    throw new Error('Formula is empty')
  }

  const operator = formula[0] as Operator
  switch (operator) {
    case 'get':
      return getOperator(data, formula, outputType)
    case 'if':
      return ifOperator(data, formula, outputType)
    case 'is':
    case 'isNot':
      return isOrIsNotOperator(data, formula, outputType)
    case 'addDays':
    case 'subtractDays':
    case 'addMonths':
    case 'subtractMonths':
      return dateMathOperator(data, formula, outputType)
    case 'switch':
      return switchOperator(data, formula, outputType)
    case 'add':
    case 'subtract':
    case 'multiply':
    case 'divide':
      return numberMathOperator(data, formula, outputType)
    case 'gt':
    case 'gte':
    case 'lt':
    case 'lte':
      return numberComparatorOperator(data, formula, outputType)
    case 'before':
    case 'after':
      return dateComparatorOperator(data, formula, outputType)
    case 'isPresent':
      return isPresentOperator(data, formula, outputType)
    case 'isNotPresent':
      return isNotPresentOperator(data, formula, outputType)
    case 'min':
      return minOperator(data, formula, outputType)
    case 'max':
      return maxOperator(data, formula, outputType)
    case 'or':
      throw new Error(`${operator} not implemented`)
    case 'and':
      throw new Error(`${operator} not implemented`)
    default:
      throw new Error(`Unknown operator ${operator}`)
  }
}

const getOperator = (data: Data, formula: any[], outputType: Type) => {
  if (formula?.length !== 2) {
    throw new Error(`get - expected 2 but found ${formula}`)
  }
  const property = formula[1]
  if (!Object.keys(data).includes(property)) {
    return {
      type: outputType.type,
      value: '',
    }
  }
  return cast(data[property], outputType)
}

const ifOperator = (data: Data, formula: any[], outputType: Type) => {
  if (formula?.length !== 4) {
    throw new Error(`if - Expected 4 but got ${formula}`)
  }
  const condition = evaluate(data, formula[1], { type: 'BOOLEAN' })
  const valueIfTrue = evaluate(data, formula[2], outputType)
  const valueIfFalse = evaluate(data, formula[3], outputType)
  return condition.value ? valueIfTrue : valueIfFalse
}

const isOrIsNotOperator = (data: Data, formula: any[], outputType: Type) => {
  const operator = formula[0] as 'is' | 'isNot'
  if (formula?.length !== 3) {
    throw new Error(`is - Expected 3 but got ${formula}`)
  }
  if (outputType.type !== 'BOOLEAN') {
    throw new Error(`iS - Expected output type BOOLEAN but got ${outputType}`)
  }

  const possibleTypes = [
    { type: 'STRING' as const },
    { type: 'NUMBER' as const },
    { type: 'BOOLEAN' as const },
    { type: 'DATE' as const },
  ]
  for (const t of possibleTypes) {
    try {
      const left = evaluate(data, formula[1], t)
      const right = evaluate(data, formula[2], t)
      const value = () => {
        switch (operator) {
          case 'is':
            return left.value === right.value
          case 'isNot':
            return left.value !== right.value
          default:
            throw new Error(`Unknown operator ${operator}`)
        }
      }
      return { type: 'BOOLEAN', value: value() }
    } catch (e) {
      // If the loop finishes we have failed to cast both values to the same type
    }
  }
  throw new Error(
    `is - Could not cast to the same type ${JSON.stringify(
      formula[1],
    )} – ${JSON.stringify(formula[2])}`,
  )
}

const dateMathOperator = (data: Data, formula: any[], outputType: Type) => {
  const operator = formula[0] as
    | 'addDays'
    | 'subtractDays'
    | 'addMonths'
    | 'subtractMonths'
  if (formula?.length !== 3) {
    throw new Error(`addDays - Expected 3 but got ${formula}`)
  }
  if (outputType.type !== 'DATE') {
    throw new Error(`addDays - Expected output type DATE but got ${outputType}`)
  }

  const date = evaluate(data, formula[1], { type: 'DATE' })
  const amount = evaluate(data, formula[2], { type: 'NUMBER' }).value

  const value = () => {
    switch (operator) {
      case 'addDays':
        return moment(date.value).add(amount, 'days').format('YYYY-MM-DD')
      case 'subtractDays':
        return moment(date.value).subtract(amount, 'days').format('YYYY-MM-DD')
      case 'addMonths':
        return moment(date.value).add(amount, 'months').format('YYYY-MM-DD')
      case 'subtractMonths':
        return moment(date.value)
          .subtract(amount, 'months')
          .format('YYYY-MM-DD')
      default:
        throw new Error(`Unknown operator ${operator}`)
    }
  }
  return {
    type: 'DATE',
    value: value(),
  }
}

const switchOperator = (data: Data, formula: any[], outputType: Type) => {
  if (formula?.length < 3) {
    throw new Error(`switch - Expected at least 3 but got ${formula}`)
  }
  const defaultExpression = formula[formula.length - 1]
  if (defaultExpression?.length !== 2) {
    throw new Error(`switch - Expected 2 but got ${defaultExpression}`)
  }
  if (defaultExpression[0] !== 'default') {
    throw new Error(`switch - Expected default but got ${defaultExpression}`)
  }
  const defaultValue = cast(defaultExpression[1], outputType)

  let switchCaseValue = null
  for (const switchCaseExpression of formula.slice(1, formula.length - 1)) {
    if (switchCaseExpression?.length !== 3) {
      throw new Error(
        `switch - Expected each case to have 2 elements but got ${switchCaseExpression}`,
      )
    }
    if (switchCaseExpression[0] !== 'case') {
      throw new Error(`switch - Expected case but got ${switchCaseExpression}`)
    }
    const condition = evaluate(data, switchCaseExpression[1], {
      type: 'BOOLEAN',
    }).value
    if (condition && !switchCaseValue) {
      switchCaseValue = evaluate(data, switchCaseExpression[2], outputType)
    }
  }
  return switchCaseValue || defaultValue
}

function numberMathOperator(data: Data, formula: any[], outputType: Type) {
  const operator = formula[0] as 'add' | 'subtract' | 'multiply' | 'divide'
  if (outputType.type !== 'NUMBER') {
    throw new Error(`${operator} - can only output NUMBER type`)
  }

  if (formula?.length !== 3) {
    throw new Error(`${operator} - Expected at 3 but got ${formula}`)
  }

  const left = evaluate(data, formula[1], { type: 'NUMBER' })
  const right = evaluate(data, formula[2], { type: 'NUMBER' })
  const value = () => {
    switch (operator) {
      case 'add':
        return left.value + right.value
      case 'subtract':
        return left.value - right.value
      case 'multiply':
        return left.value * right.value
      case 'divide':
        if (right.value === 0) {
          throw new Error(`${operator} - cannot divide by 0`)
        }
        return left.value / right.value
      default:
        throw new Error(`Unknown operator ${operator}`)
    }
  }
  return {
    type: 'NUMBER',
    value: value(),
  }
}

function numberComparatorOperator(
  data: Data,
  formula: any[],
  outputType: Type,
) {
  const operator = formula[0] as 'gt' | 'gte' | 'lt' | 'lte'
  if (outputType.type !== 'BOOLEAN') {
    throw new Error(`${operator} - can only output BOOLEAN type`)
  }
  if (formula?.length !== 3) {
    throw new Error(`${operator} - Expected at 3 but got ${formula}`)
  }

  const left = evaluate(data, formula[1], { type: 'NUMBER' })
  const right = evaluate(data, formula[2], { type: 'NUMBER' })
  const value = () => {
    switch (operator) {
      case 'gt':
        return left.value > right.value
      case 'gte':
        return left.value > right.value || left.value === right.value
      case 'lt':
        return left.value < right.value
      case 'lte':
        return left.value < right.value || left.value === right.value
      default:
        throw new Error(`Unknown operator - ${operator}`)
    }
  }

  return {
    type: 'BOOLEAN',
    value: value(),
  }
}

function dateComparatorOperator(data: Data, formula: any[], outputType: Type) {
  const operator = formula[0] as 'before' | 'after'
  if (outputType.type !== 'BOOLEAN') {
    throw new Error(
      `${operator} - can only output BOOLEAN type, got ${outputType.type}`,
    )
  }
  if (formula?.length !== 3) {
    throw new Error(`${operator} - Expected 3 operands but got ${formula}`)
  }

  const left = evaluate(data, formula[1], { type: 'DATE' })
  const right = evaluate(data, formula[2], { type: 'DATE' })

  const value = () => {
    switch (operator) {
      case 'before':
        return left.value < right.value
      case 'after':
        return left.value > right.value
      default:
        throw new Error(`Unknown operator - ${operator}`)
    }
  }
  return {
    type: 'BOOLEAN',
    value: value(),
  }
}

const minOperator = (data: Data, formula: any[], outputType: Type) => {
  if (outputType.type !== 'NUMBER') {
    throw new Error(`min - can only output NUMBER type`)
  }
  if (formula?.length < 2) {
    throw new Error(`min - Expected at least 2 but got ${formula}`)
  }
  const values = formula
    .slice(1)
    .map((f) => evaluate(data, f, { type: 'NUMBER' }))
  const value = Math.min(...values.map((v) => v.value))
  return {
    type: 'NUMBER',
    value,
  }
}

const maxOperator = (data: Data, formula: any[], outputType: Type) => {
  if (outputType.type !== 'NUMBER') {
    throw new Error(`max - can only output NUMBER type`)
  }
  if (formula?.length < 2) {
    throw new Error(`max - Expected at least 2 but got ${formula}`)
  }
  const values = formula
    .slice(1)
    .map((f) => evaluate(data, f, { type: 'NUMBER' }))
  const value = Math.max(...values.map((v) => v.value))
  return {
    type: 'NUMBER',
    value,
  }
}

const isPresentOperator = (data: Data, formula: any[], outputType: Type) => {
  if (formula?.length !== 2) {
    throw new Error(`isPresent - Expected 3 operands but got ${formula}`)
  }

  const value = evaluate(data, formula[1], { type: 'STRING' })
  console.log(value)
  return {
    type: 'BOOLEAN',
    value: !!value?.value,
  }
}

const isNotPresentOperator = (data: Data, formula: any[], outputType: Type) => {
  if (formula?.length !== 2) {
    throw new Error(`isPresent - Expected 3 operands but got ${formula}`)
  }

  const value = evaluate(data, formula[1], { type: 'STRING' })
  return {
    type: 'BOOLEAN',
    value: !value?.value,
  }
}
